import { useCallback, useRef } from "react";
import { useForm } from "@mantine/form";
import { Editor } from "@tiptap/react";
import { Group, Input, MultiSelect, TextInput } from "@mantine/core";
import { EventFields, NewRequestFormProps } from "../../types/request.types";
import ContentEditor from "../ContentEditor";
import CampusSelect from "../CampusSelect";
import { DatePickerInput, TimeInput } from "@mantine/dates";
import FormAttachments from "../attachments/FormAttachments";
import FormContacts from "../contacts/FormContacts";
import { ActionBar } from "./ActionBar";
import { useConvertFrom } from "../../hooks/use-convert-from";

export default function EventForm(props: NewRequestFormProps) {
  const { onSubmit, tempId, fromRequest } = props;
  const descEditorRef = useRef<Editor>(null);
  const whyExcitingEditorRef = useRef<Editor>(null);
  const form = useForm<EventFields>({
    mode: 'uncontrolled',
    initialValues: {
      name: '',
      description: '',
      sponsors: '',
      audience: [],
      campus_id: null,
      location: '',
      start_date: null,
      start_time: null,
      end_date: null,
      end_time: null,
      cost: '',
      why_exciting: '',
      marketing_types: [],
      requested_completion_date: null
    },
    validate: {
      description: val => (
        descEditorRef.current && descEditorRef.current.getText().length > 0 ?
          null : 'Please fill out this field'
      ),
      requested_completion_date: val => (
        val ? null : 'Please fill out this field'
      )
    }
  });
  useConvertFrom<EventFields>(form, descEditorRef.current, fromRequest);

  const onFormSubmit = useCallback((submitVals: EventFields) => {
    const newRequest = { ...submitVals };
    newRequest.description = descEditorRef.current?.getHTML();
    newRequest.why_exciting = whyExcitingEditorRef.current?.getHTML();
    onSubmit(newRequest);
  }, []);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      {!fromRequest && (
        <Input.Wrapper
          label="Contacts"
          mt="md"
        >
          <FormContacts<EventFields> form={form} />
        </Input.Wrapper>
      )}

      <TextInput
        {...form.getInputProps('name')}
        key={form.key('name')}
        label="Event name"
        mt="md"
        required
      />

      <ContentEditor<EventFields>
        formKey="description"
        label="Description"
        required
        form={form} 
        editorRef={descEditorRef}
        mt="md"
      />

      <TextInput
        {...form.getInputProps('sponsors')}
        key={form.key('sponsors')}
        label="Sponsors"
        mt="md"
      />

      <MultiSelect
        {...form.getInputProps('audience')}
        key={form.key('audience')}
        label="Audience"
        placeholder="Select all that apply"
        mt="md"
        data={[
          'JCC students',
          'JCC students, faculty, and staff',
          'Prospective students',
          'Alumni',
          'Community members'
        ]}
      />
      
      <MultiSelect
        {...form.getInputProps('marketing_types')}
        key={form.key('marketing_types')}
        label="Types of marketing"
        placeholder="Select all that apply"
        mt="md"
        data={[
          'News release',
          'Organic social media',
          'Website calendar'
        ]}
      />

      <CampusSelect
        {...form.getInputProps('campus_id')}
        key={form.key('campus_id')}
        label="Campus"
        mt="md"
      />

      <TextInput
        {...form.getInputProps('location')}
        key={form.key('location')}
        label="Location"
        mt="md"
      />

      <Group mt="md">
        <DatePickerInput
          {...form.getInputProps('start_date')}
          key={form.key('start_date')}
          label="Start date"
          highlightToday
          miw={200}
        />

        <TimeInput
          {...form.getInputProps('start_time')}
          key={form.key('start_time')}
          label="Start time"
        />
      </Group>

      <Group mt="md">
        <DatePickerInput
          {...form.getInputProps('end_date')}
          key={form.key('end_date')}
          label="End date"
          highlightToday
          miw={200}
        />

        <TimeInput
          {...form.getInputProps('end_time')}
          key={form.key('end_time')}
          label="End time"
        />
      </Group>


      <TextInput
        {...form.getInputProps('cost')}
        key={form.key('cost')}
        label="Cost"
        mt="md"
      />

      <ContentEditor<EventFields>
        formKey="why_exciting"
        label="Why is it exciting for the JCC community?"
        form={form} 
        editorRef={whyExcitingEditorRef}
        mt="md"
      />

      <DatePickerInput
        {...form.getInputProps('requested_completion_date')}
        key={form.key('requested_completion_date')}
        label="Requested completion date"
        required
        highlightToday
        mt="md"
      />

      {!fromRequest && (
        <Input.Wrapper
          label="Attachments"
          mt="md"
        >
          <FormAttachments tempId={tempId} />
        </Input.Wrapper>
      )}

      <ActionBar showConvertNotice={!!fromRequest} />
    </form>
  );
}
